import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { DietMenuItem } from "@/features/diets/components/diet-menu-item";
import type { DietCatalogDetail } from "@/features/diets/types";
import type { DietMenuPreviewMeals } from "@/features/diets/types/diet-menu-preview.type";
import { useCarouselDrag } from "@/lib/hooks/use-carousel-drag";
import { cn } from "@/lib/utils";

type Props = {
  meal: DietMenuPreviewMeals;
  setActiveMealIngredients: (mealId: string, dishId: string) => void;
  activeAllergensAlert?: boolean;
  type?: "multidiet" | "default";
  data: DietCatalogDetail;
};

export const DietMenuWrapper = ({
  meal,
  setActiveMealIngredients,
  activeAllergensAlert,
  type,
  data,
}: Props) => {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  const menuSelection = meal.dishes.length > 1;

  const isDragging = useCarouselDrag(api);

  useEffect(() => {
    if (!api) {
      return;
    }
    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap());

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);
  return (
    <>
      <div className="space-y-2">
        <p className="font-medium text-brand-green1-300 text-xl">{meal.label}</p>
        {menuSelection ? (
          <Carousel
            className="relative"
            opts={{ dragFree: false, slidesToScroll: 1, align: "start" }}
            setApi={setApi}
          >
            <CarouselContent className="mr-4 md:space-x-2">
              {meal.dishes.map((dish) => (
                <CarouselItem
                  className={cn(
                    "basis-[97%] md:basis-[37%]",
                    type === "multidiet" && "md:basis-[45%]",
                  )}
                  key={dish.id}
                >
                  <DietMenuItem
                    activeAllergensAlert={activeAllergensAlert}
                    data={data}
                    dish={dish}
                    meal={meal}
                    menuSelection={menuSelection}
                    setActiveMealIngredients={setActiveMealIngredients}
                  />
                </CarouselItem>
              ))}
            </CarouselContent>
            {!isDragging && (
              <>
                <Button
                  className={cn(
                    "absolute top-1/2 left-0 z-10 hidden h-12 w-12 -translate-y-1/2 rounded-xl border bg-brand-white p-1",
                    current > 0 && "flex",
                  )}
                  onClick={() => api?.scrollTo(current - 1)}
                  size="lg"
                  type="button"
                  variant="ghost"
                >
                  <IconChevronLeft className="h-6 w-6" />
                </Button>
                <Button
                  className={cn(
                    "absolute top-1/2 right-0 z-10 hidden h-12 w-12 -translate-y-1/2 rounded-xl border bg-brand-white p-1",
                    current < count - 1 && "flex",
                  )}
                  onClick={() => api?.scrollTo(current + 1)}
                  size="lg"
                  type="button"
                  variant="ghost"
                >
                  <IconChevronRight className="h-6 w-6" />
                </Button>
              </>
            )}
          </Carousel>
        ) : (
          <DietMenuItem
            activeAllergensAlert={activeAllergensAlert}
            data={data}
            dish={meal.dishes[0]}
            key={meal.mealId}
            meal={meal}
            setActiveMealIngredients={setActiveMealIngredients}
          />
        )}
      </div>
    </>
  );
};
